import { HttpClient } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { firstValueFrom } from 'rxjs';
import packageInfo from '../../../../package.json';
import { eventLogToPayload } from '../api/adapters';
import { EventEntry } from '../models/event-log.model';
import { environment } from '../../../environments/environment';
import { DocumentDownloadResponse } from '../models/document.model';
import { AuthService } from './auth.service';
import { GpsService } from './gps.service';
import { NetworkService } from './network.service';
import { storagePrefix } from '../utils/storage-key';

/** A queued batch of event entries for a specific server, persisted when offline. */
interface PendingLogBatch { serverId: string; entries: EventEntry[]; }

/**
 * Builds the user-scoped SecureStorage key holding pending event-log batches.
 *
 * @param entityId - Process-server entity ID of the authenticated user.
 * @returns The `{hashedEntityId}_pending_event_logs` key.
 */
function pendingLogKey(entityId: number): string {
  return `${storagePrefix(entityId)}_pending_event_logs`;
}

/**
 * Total number of event entries across all batches.
 *
 * @param batches - Pending batches.
 * @returns Sum of every batch's entry count.
 */
function countEntries(batches: PendingLogBatch[]): number {
  return batches.reduce((sum, b) => sum + (b.entries?.length ?? 0), 0);
}

/**
 * Removes exactly one occurrence of each `sent` batch from `current`.
 *
 * @remarks
 * Matching is by serialised content rather than by array position, so
 * batches appended while a flush was in flight (or a storage reset between
 * read and write) are never mistaken for sent ones and silently dropped.
 *
 * @param current - Batches currently in storage.
 * @param sent    - Batches the server confirmed during this flush.
 * @returns The batches that still need sending, in their original order.
 */
function withoutSent(current: PendingLogBatch[], sent: PendingLogBatch[]): PendingLogBatch[] {
  const pending = sent.map(b => JSON.stringify(b));
  return current.filter(batch => {
    const i = pending.indexOf(JSON.stringify(batch));
    if (i === -1) return true;
    pending.splice(i, 1);
    return false;
  });
}

/** Resolved device context shared across all event entries in a single flush. */
interface DeviceContext {
  appVersion:     string;
  deviceId:       string;
  devicePlatform: string;
  latitude:       string;
  longitude:      string;
  ipAddress:      string;
}

/**
 * Sends event log entries to the server after key user actions.
 *
 * @remarks
 * Methods are fire-and-forget — they do not throw. HTTP failures are logged
 * to `console.warn` only. Always call with `void` to make that intent explicit.
 *
 * Device context (app version, device ID, GPS coords) is collected once per
 * call and shared across all entries in the batch.
 */
@Injectable({ providedIn: 'root' })
export class EventLogService {
  private readonly http           = inject(HttpClient);
  private readonly authService    = inject(AuthService);
  private readonly gpsService     = inject(GpsService);
  private readonly networkService = inject(NetworkService);

  /**
   * The flush currently in progress, shared by concurrent callers so a
   * reconnect, a sync cycle and the Upqueue "Transmit all" button all await
   * the same run instead of POSTing the same batches twice.
   */
  private flushInFlight: Promise<void> | null = null;

  /**
   * Serialises every read-modify-write of the pending-log storage key, so an
   * entry saved while a flush is finishing is never overwritten or removed.
   */
  private storageChain: Promise<unknown> = Promise.resolve();

  private readonly _pendingCount = signal(0);
  private readonly _flushing     = signal(false);

  /**
   * Number of event entries persisted locally and not yet confirmed by the
   * server. Used by the logout warning sheet and the Upqueue page. Entries
   * survive logout and are flushed after the same user's next sign-in (the
   * online `effect()` re-runs when the token changes).
   */
  readonly pendingCount = this._pendingCount.asReadonly();

  /** `true` while a flush is POSTing batches to the server. */
  readonly flushing = this._flushing.asReadonly();

  constructor() {
    // Auto-flush queued entries whenever the device comes back online.
    effect(() => {
      if (this.networkService.isOnline()) void this.flushPending();
    });

    // Keep pendingCount in step with the signed-in user's stored queue.
    effect(() => {
      const entityId = this.authService.token()?.user?.entityId;
      if (entityId == null) {
        this._pendingCount.set(0);
        return;
      }
      void this.refreshPendingCount(entityId);
    });
  }

  /**
   * Logs a `jobaccepted` event for each accepted job number.
   * Fire-and-forget — always call with `void`.
   *
   * @param jobIds - Job IDs (string job numbers) that were just accepted.
   */
  async logJobsAccepted(jobIds: string[]): Promise<void> {
    if (jobIds.length === 0) return;
    const token = this.authService.token();
    if (!token) return;

    const ctx     = await this.collectContext();
    const entries = jobIds.map(jobId => this.buildEntry(jobId, 'jobaccepted', 'Job Accepted', ctx));
    await this.postOrQueue(String(token.user.entityId), entries);
  }

  /**
   * Logs a `printworkorder` event for each printed job.
   * Message is derived from `workorder_documents[0].document_message` in the
   * download response (`"Work Order GENERATED"` → `"Work Order PRINTED"` or
   * `"Work Order RE-PRINTED"`). Falls back to `"Work Order PRINTED"` /
   * `"Work Order RE-PRINTED"` when no document message is available.
   * Posts directly (fire-and-forget) — always call with `void`.
   *
   * @remarks
   * `isReprint` is **per job**, not a single flag for the whole batch
   * (2026-07-28 fix) — a "Print N jobs" batch can legitimately mix jobs
   * being printed for the first time with jobs already printed at least
   * once before (a genuine reprint, under the configurable reprint
   * threshold — see `PrintQueueComponent`/`PrintService`). A single
   * batch-wide flag couldn't represent that mix correctly. The caller
   * decides `isReprint` per job — `PrintQueueComponent` passes
   * `j.printCount > 0` using each job's *pre-print* count (the second,
   * third, etc. print of the same job is a reprint; the first is not).
   *
   * @param jobs      - Jobs that were just printed (`{ id, jobNum, isReprint }`).
   * @param response  - Optional download API response used to resolve per-job messages.
   *                    When omitted, defaults to generic `"Work Order PRINTED"` / `"Work Order RE-PRINTED"`.
   */
  async logJobsPrinted(
    jobs:      Array<{ id: string; jobNum: string; isReprint: boolean }>,
    response?: DocumentDownloadResponse,
  ): Promise<void> {
    if (jobs.length === 0) return;
    const token = this.authService.token();
    if (!token) return;

    const ctx     = await this.collectContext();
    const entries = jobs.map(job => {
      const item        = response?.data.find(d => d.jobNumber === Number(job.jobNum));
      const rawMsg       = item?.workorderDocuments[0]?.documentMessage ?? '';
      const replacement  = job.isReprint ? 'RE-PRINTED' : 'PRINTED';
      const message      = rawMsg
        ? rawMsg.replace('GENERATED', replacement)
        : `Work Order ${replacement}`;
      return this.buildEntry(job.id, 'printworkorder', message, ctx);
    });
    await this.postOrQueue(String(token.user.entityId), entries);
  }

  /**
   * Logs a `printservicedocuments` event for each job whose Service
   * Documents packet was just printed (MOB-315).
   *
   * @remarks
   * Distinct from {@link logJobsPrinted}'s `printworkorder` event — that one
   * logs the Work Order (`WO`) half of the printed packet with a per-job
   * message derived from the download response; this one logs the Service
   * Documents (`SD`) half with a single fixed message per the ticket's exact
   * wording, `"ServiceDocuments packet printed by serverId: {entityId}"` —
   * identical across every job in the batch, since the requested text only
   * ever varies by server ID, not by job. Both events are posted for the
   * same print action (`PrintService.downloadDocuments()` always requests
   * `document_code: ['SD', 'WO']` together), so call this alongside
   * `logJobsPrinted()`, not instead of it.
   * Posts directly (fire-and-forget) — always call with `void`.
   *
   * @param jobIds - Job IDs whose Service Documents packet was just printed.
   */
  async logServiceDocumentsPrinted(jobIds: string[]): Promise<void> {
    if (jobIds.length === 0) return;
    const token = this.authService.token();
    if (!token) return;

    const ctx     = await this.collectContext();
    const message = `ServiceDocuments packet printed by serverId: ${token.user.entityId}`;
    const entries = jobIds.map(jobId => this.buildEntry(jobId, 'printservicedocuments', message, ctx));
    await this.postOrQueue(String(token.user.entityId), entries);
  }

  /**
   * Logs a `printaffidavit` event when a signed affidavit is submitted.
   * Message is derived from `documentMessage` on the affidavit record
   * (`"GENERATED"` is replaced with `"PRINTED"` and `" (SIGNED)"` appended).
   * Falls back to `"Affidavit SIGNED"` when no message is available.
   * Fire-and-forget — always call with `void`.
   *
   * @param jobId       - Job ID for which the affidavit was signed.
   * @param docMessage  - Raw document message from the API (may be empty).
   */
  async logJobSigned(jobId: string, docMessage: string): Promise<void> {
    const token = this.authService.token();
    if (!token) return;

    const message = docMessage
      ? docMessage.replace('GENERATED', 'PRINTED') + ' (SIGNED)'
      : 'Affidavit SIGNED';
    const ctx     = await this.collectContext();
    const entries = [this.buildEntry(jobId, 'printaffidavit', message, ctx)];
    await this.postOrQueue(String(token.user.entityId), entries);
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Gathers app version, device ID, platform, and best-available GPS
   * coordinates. Failures in any individual call are mapped to empty strings
   * so the event log is never blocked.
   */
  private async collectContext(): Promise<DeviceContext> {
    const [deviceIdResult, platformResult, coordsResult, ipResult] = await Promise.allSettled([
      Device.getId().then(i => i.identifier),
      Device.getInfo().then(i => i.platform),
      this.bestCoords(),
      this.getIpAddress(),
    ]);

    return {
      appVersion:     packageInfo.version ?? 'Unknown Version',
      deviceId:       deviceIdResult.status === 'fulfilled' ? deviceIdResult.value      : '',
      devicePlatform: platformResult.status === 'fulfilled' ? platformResult.value      : '',
      latitude:       coordsResult.status   === 'fulfilled' ? coordsResult.value.lat   : '',
      longitude:      coordsResult.status   === 'fulfilled' ? coordsResult.value.lng   : '',
      ipAddress:      ipResult.status       === 'fulfilled' ? ipResult.value           : '',
    };
  }

  /**
   * Resolves the device's local IP address using WebRTC ICE candidate discovery.
   * No external network call is made — the browser negotiates locally.
   * Returns `''` when WebRTC is unavailable (native iOS/Android, old browsers)
   * or when no host candidate is found within 5 seconds.
   */
  private getIpAddress(): Promise<string> {
    return new Promise<string>(resolve => {
      try {
        const RTC: typeof RTCPeerConnection | undefined =
          window.RTCPeerConnection               ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).mozRTCPeerConnection   ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).webkitRTCPeerConnection;

        if (!RTC) { resolve(''); return; }

        const pc       = new RTC({ iceServers: [] });
        const ipRegex  = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;

        pc.createDataChannel('');

        pc.onicecandidate = (ice: RTCPeerConnectionIceEvent) => {
          if (!ice?.candidate?.candidate) return;
          const ip = ipRegex.exec(ice.candidate.candidate)?.[1];
          if (ip) { resolve(ip); pc.onicecandidate = null; }
        };

        pc.createOffer()
          .then((sdp: RTCSessionDescriptionInit) => {
            sdp.sdp?.split('\n').forEach(line => {
              if (line.includes('candidate')) {
                const addr = line.split(' ')[4];
                const type = line.split(' ')[7];
                if (ipRegex.test(addr) && type === 'host') resolve(addr);
              }
            });
            return pc.setLocalDescription(sdp);
          })
          .catch(() => resolve(''));

        setTimeout(() => resolve(''), 5000);
      } catch {
        resolve('');
      }
    });
  }

  /**
   * Returns the best available GPS coordinates.
   * Uses the already-captured `GpsService` fix if present; otherwise fires a
   * quick single-attempt `getCurrentPosition` with a 5-second timeout.
   * Returns `{ lat: '', lng: '' }` on any failure.
   */
  private async bestCoords(): Promise<{ lat: string; lng: string }> {
    const cached = this.gpsService.coordinates();
    if (cached) {
      return { lat: String(cached.latitude), lng: String(cached.longitude) };
    }
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 5000 });
      return {
        lat: String(pos.coords.latitude),
        lng: String(pos.coords.longitude),
      };
    } catch {
      return { lat: '', lng: '' };
    }
  }

  /**
   * Assembles a single `EventEntry` from shared device context and
   * event-specific fields.
   *
   * @param jobId   - Job ID associated with the event.
   * @param action  - Machine-readable action code (e.g. `'jobaccepted'`).
   * @param message - Human-readable description.
   * @param ctx     - Shared device context collected once per batch.
   */
  private buildEntry(
    jobId: string,
    action: string,
    message: string,
    ctx: DeviceContext,
  ): EventEntry {
    return {
      jobId,
      action,
      message,
      appVersion:     ctx.appVersion,
      deviceId:       ctx.deviceId,
      devicePlatform: ctx.devicePlatform,
      latitude:       ctx.latitude,
      longitude:      ctx.longitude,
      ipAddress:      ctx.ipAddress,
      timestamp:      this.formatTimestamp(new Date()),
    };
  }

  /**
   * Formats a date as `"MM/DD/YYYY HH:MM:SS"` (local time) for the API timestamp field.
   *
   * @param date - The date to format.
   */
  private formatTimestamp(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const mm   = pad(date.getMonth() + 1);
    const dd   = pad(date.getDate());
    const yyyy = date.getFullYear();
    const hh   = pad(date.getHours());
    const min  = pad(date.getMinutes());
    const ss   = pad(date.getSeconds());
    return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
  }

  /**
   * Always persists entries to SecureStorage first (queue-first), then
   * immediately flushes when the device is online.
   *
   * Mirrors the Submissions pattern: entries are never lost even if the
   * network drops between the online check and the HTTP call. When offline
   * the flush is deferred to the next reconnect event or SyncService P2 cycle.
   *
   * @param serverId - Process-server entity ID for the URL path.
   * @param entries  - Event entries to submit.
   */
  private async postOrQueue(serverId: string, entries: EventEntry[]): Promise<void> {
    await this.saveToQueue(serverId, entries);
    if (this.networkService.isOnline()) {
      void this.flushPending();
    }
  }

  /**
   * Appends a batch of entries to the persistent offline queue.
   *
   * @param serverId - Server entity ID stored alongside entries so the flush
   *                   path knows which endpoint to POST to.
   * @param entries  - Entries to persist.
   */
  private async saveToQueue(serverId: string, entries: EventEntry[]): Promise<void> {
    const token = this.authService.token();
    if (!token) return;
    const entityId = token.user.entityId;
    const key = pendingLogKey(entityId);
    try {
      await this.withStorageLock(async () => {
        const existing = await this.readBatches(key);
        await this.writeBatches(key, [...existing, { serverId, entries }], entityId);
      });
    } catch (err) {
      console.warn('[EventLogService] Failed to persist pending entries:', err);
    }
  }

  /**
   * Sends all stored pending batches to the API, removing each one only after
   * the server confirms it.
   *
   * @remarks
   * Called automatically when `NetworkService.isOnline` becomes `true`, by the
   * `SyncService` flush cycle, and by the Upqueue page's "Transmit all" button.
   * Concurrent callers share one in-flight run (see {@link flushInFlight}).
   *
   * Batches are POSTed one at a time; a failed batch stays queued while the
   * others are still sent and removed, so a partial failure never re-sends
   * batches the server already accepted. Entries saved while the flush is in
   * flight are preserved — see {@link withoutSent}.
   *
   * Never throws — failures leave entries queued for the next attempt.
   *
   * @returns Resolves once the flush has finished (or immediately when there
   *          is nothing to send / no signed-in user).
   */
  flushPending(): Promise<void> {
    this.flushInFlight ??= this.runFlush().finally(() => {
      this.flushInFlight = null;
      this._flushing.set(false);
    });
    return this.flushInFlight;
  }

  /**
   * Performs one flush pass for the signed-in user — see {@link flushPending}.
   */
  private async runFlush(): Promise<void> {
    const entityId = this.authService.token()?.user?.entityId;
    if (entityId == null) return;
    const key = pendingLogKey(entityId);
    try {
      const snapshot = await this.withStorageLock(() => this.readBatches(key));
      if (snapshot.length === 0) return;
      this._flushing.set(true);
      const sent = await this.postBatches(snapshot);
      if (sent.length === 0) return;
      // Always update the OWNER's key, even if they logged out mid-flush —
      // pending logs survive logout, so skipping this would re-send the
      // confirmed batches after their next sign-in.
      await this.withStorageLock(async () => {
        const current = await this.readBatches(key);
        await this.writeBatches(key, withoutSent(current, sent), entityId);
      });
    } catch (err) {
      console.warn('[EventLogService] Flush failed — will retry:', err);
    }
  }

  /**
   * POSTs each batch sequentially to `POST /process-server/{id}/eventlog/add`.
   *
   * @param batches - Batches to send, oldest first.
   * @returns The batches the server confirmed; failed ones are omitted.
   */
  private async postBatches(batches: PendingLogBatch[]): Promise<PendingLogBatch[]> {
    const sent: PendingLogBatch[] = [];
    for (const batch of batches) {
      try {
        await firstValueFrom(
          this.http.post<unknown>(
            `${environment.apiUrl}/process-server/${batch.serverId}/eventlog/add`,
            eventLogToPayload(batch.entries),
          ),
        );
        sent.push(batch);
      } catch { /* keep this batch for the next attempt */ }
    }
    return sent;
  }

  /**
   * Re-reads the stored queue and updates {@link pendingCount}.
   *
   * @param entityId - User whose queue to count; ignored if the session has
   *                   changed by the time the read completes.
   */
  private async refreshPendingCount(entityId: number): Promise<void> {
    const batches = await this.withStorageLock(() => this.readBatches(pendingLogKey(entityId)));
    if (this.authService.token()?.user?.entityId === entityId) {
      this._pendingCount.set(countEntries(batches));
    }
  }

  /**
   * Runs `task` after every previously scheduled storage task has settled.
   *
   * @param task - Read-modify-write against the pending-log key.
   * @returns The task's result.
   */
  private withStorageLock<T>(task: () => Promise<T>): Promise<T> {
    const run = this.storageChain.then(task, task);
    this.storageChain = run.catch(() => undefined);
    return run;
  }

  /**
   * Reads the pending batches stored under `key`.
   *
   * @param key - Pending-log storage key.
   * @returns Stored batches, or `[]` when the key is absent or unreadable.
   */
  private async readBatches(key: string): Promise<PendingLogBatch[]> {
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      const parsed: unknown = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as PendingLogBatch[]) : [];
    } catch {
      return []; // key absent on first write / after a successful flush
    }
  }

  /**
   * Persists `batches` under `key` (removing the key when empty) and updates
   * {@link pendingCount}.
   *
   * {@link pendingCount} is only updated when `entityId` is still the
   * signed-in user — a write that lands after logout must not show the
   * previous user's count.
   *
   * @param key      - Pending-log storage key.
   * @param batches  - Full replacement list of pending batches.
   * @param entityId - Owner of `key`.
   */
  private async writeBatches(key: string, batches: PendingLogBatch[], entityId: number): Promise<void> {
    if (batches.length === 0) {
      await SecureStoragePlugin.remove({ key }).catch(() => undefined);
    } else {
      await SecureStoragePlugin.set({ key, value: JSON.stringify(batches) });
    }
    if (this.authService.token()?.user?.entityId === entityId) {
      this._pendingCount.set(countEntries(batches));
    }
  }
}
